import type { Theme } from '@mui/material/styles';
import type { LinkProps } from '@mui/material/Link';

import { mergeClasses } from 'minimal-shared/utils';
import { useState, useEffect, forwardRef } from 'react';

import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';

import { useLogoConfig } from 'src/hooks/use-logo-config';

import { CONFIG } from 'src/global-config';

import { useSettingsContext } from 'src/components/settings';

import { logoClasses } from './classes';

// ----------------------------------------------------------------------

export type LogoProps = LinkProps & {
  isSingle?: boolean;
  disabled?: boolean;
  isLogin?: boolean;
};

export const Logo = forwardRef<HTMLAnchorElement, LogoProps>((props, ref) => {
  const { className, href = '/', isSingle = true, disabled, isLogin, sx, ...other } = props;

  const logoConfig = useLogoConfig();
  const { state } = useSettingsContext();

  const [logoKey, setLogoKey] = useState(0);

  const isDarkMode = state?.colorScheme === 'dark';
  const shouldUseDarkLogo = isDarkMode && logoConfig.darkModeFullLogo;
  const shouldUseDarkSingleLogo = isDarkMode && logoConfig.darkModeSingleLogo;

  useEffect(() => {
    if (state?.primaryColor) setLogoKey((k) => k + 1);
  }, [state?.primaryColor]);

  const loginLogo = (
    <img
      key={`login-logo-${logoKey}`}
      alt="Login logo"
      src={`${CONFIG.assetsDir}/logo/logo-xpanse.svg`}
    />
  );

  const singleLogoSrc = shouldUseDarkSingleLogo
    ? logoConfig.darkModeSingleLogo
    : logoConfig.singleLogo;

  const singleLogo = (
    <img
      key={`single-logo-${logoKey}`}
      alt="Single logo"
      src={`${CONFIG.assetsDir}/logo/${singleLogoSrc}`}
      style={{ width: '100%', maxHeight: '100%' }}
    />
  );

  const fullLogoSrc = shouldUseDarkLogo ? logoConfig.darkModeFullLogo : logoConfig.fullLogo;

  const fullLogo = (
    <img
      key={`full-logo-${logoKey}`}
      alt="Full logo"
      src={`${CONFIG.assetsDir}/logo/${fullLogoSrc}`}
      style={{ width: '100%', height: 'auto', maxWidth: 160, maxHeight: 60 }}
    />
  );

  return (
    <LogoRoot
      ref={ref}
      component={RouterLink}
      href={href}
      aria-label="Logo"
      underline="none"
      className={mergeClasses([logoClasses.root, className])}
      sx={[
        (theme: Theme) => ({
          transition: theme.transitions.create(['width', 'height'], {
            duration: theme.transitions.duration.shorter,
          }),

          ...((isLogin === true || (isLogin === undefined && !isSingle)) && {
            display: 'flex',
          }),
          ...(disabled && { pointerEvents: 'none' }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {isLogin === true ? loginLogo : isSingle ? singleLogo : fullLogo}
    </LogoRoot>
  );
});

// ----------------------------------------------------------------------

const LogoRoot = styled(Link)(() => ({
  flexShrink: 0,
}));
